import React from "react";
import {useSelector} from "react-redux";
import {getMatchesById, getPlayerById, getPlayerStats} from "modules/selectors";
import {getPlayerPositionMap} from "modules/utils";
import styled from "styled-components";
import {Exist} from "components";
import {media} from "assets/media";
import {useTranslation} from "react-i18next";

const Wrapper = styled.div`
	width: 100%;
	overflow: auto;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	min-width: 100%;

	@media screen and ${media.lessThanMD} {
		.sticky {
			left: 0;
			position: sticky;
			z-index: 9999;
			background: #ffffff;

			&.second {
				left: 64px;
				padding-right: 12px;

				&:after {
					content: "";
					position: absolute;
					right: -20px;
					top: 0;
					width: 20px;
					height: 100%;
					opacity: 0.55;
					background: linear-gradient(90deg, #dddddd 0%, rgba(238, 238, 238, 0) 100%);
				}
			}
		}
		td,
		th {
			min-width: 64px;
		}
	}
`;

const Thead = styled.thead`
	border-bottom: 1px solid #dddddd;

	tr th {
		color: ${({theme}) => theme.text_color};
		font-size: 14px;
		font-weight: bold;
		letter-spacing: 0;
		line-height: 17px;
		padding: 10px 4px;
		text-align: center;
		white-space: nowrap;
	}
`;

const TBody = styled.tbody`
	tr {
		border-bottom: 1px solid #dddddd;

		td {
			color: ${({theme}) => theme.text_color};
			font-size: 14px;
			font-weight: bold;
			letter-spacing: 0;
			line-height: 17px;
			padding: 10px 4px;
			text-align: center;
			white-space: nowrap;

			&.empty {
				padding: 12px 0;
				font-weight: 400;
			}
		}
	}
`;

interface IProps {
	playerId: number;
}

export const PlayerDetails: React.FC<IProps> = ({playerId}) => {
	const player = useSelector(getPlayerById)(playerId);
	const stats = useSelector(getPlayerStats);
	const matches = useSelector(getMatchesById);
	const {isGoalkeeper, isMidfielder, isForward} = getPlayerPositionMap(player?.position);
	const isMidfielderOrForward = isMidfielder || isForward;
	const {t} = useTranslation();

	const getOpposition = (id: number) => {
		const match = matches[id];

		if (!match) {
			return "-";
		}

		const isHome = match.home_squad_id === player.squadId;
		return isHome ? match.away_squad_name : match.home_squad_name;
	};

	const isStatsVisible = stats.length !== 0 && Array.isArray(stats);

	return (
		<Wrapper className="detail">
			<Table>
				<Thead>
					<tr>
						<th className="sticky">{t("player_profiles.detail_stats.th_round")}</th>
						<th className="sticky second">
							{t("player_profiles.detail_stats.th_opp")}
						</th>
						<th>{t("player_profiles.detail_stats.th_points")}</th>
						<th>{t("player_profiles.detail_stats.th_minutes")}</th>
						<Exist when={!isGoalkeeper}>
							<th>{t("player_profiles.detail_stats.th_goals")}</th>
							<th>{t("player_profiles.detail_stats.th_assists")}</th>
						</Exist>
						<Exist when={isGoalkeeper}>
							<th>{t("player_profiles.detail_stats.th_penalties")}</th>
							<th>{t("player_profiles.detail_stats.th_saves")}</th>
						</Exist>
						<Exist when={isMidfielderOrForward}>
							<th>{t("player_profiles.detail_stats.th_clean_sheets")}</th>
						</Exist>
						<th>{t("player_profiles.detail_stats.th_own_goals")}</th>
						<th>{t("player_profiles.detail_stats.th_yellows")}</th>
						<th>{t("player_profiles.detail_stats.th_reds")}</th>
					</tr>
				</Thead>
				<TBody>
					<Exist when={stats.length === 0}>
						<tr>
							<td className="empty" colSpan={11}>
								Nothing to show here...
							</td>
						</tr>
					</Exist>
					<Exist when={isStatsVisible}>
						{Array.isArray(stats) &&
							stats.map((statItem) => (
								<tr key={statItem.match_id}>
									<td className="sticky">{statItem.round}</td>
									<td className="sticky second">
										{getOpposition(statItem.match_id)}
									</td>
									<td>{statItem.points}</td>
									<td>{statItem.stats.MIN}</td>
									<Exist when={!isGoalkeeper}>
										<td>{statItem.stats.GS}</td>
										<td>{statItem.stats.AS}</td>
									</Exist>
									<Exist when={isGoalkeeper}>
										<td>{statItem.stats.SP}</td>
										<td>{statItem.stats.SV}</td>
									</Exist>
									<Exist when={isMidfielderOrForward}>
										<td>{statItem.stats.CS}</td>
									</Exist>
									<td>{statItem.stats.OG}</td>
									<td>{statItem.stats.YC}</td>
									<td>{statItem.stats.RC}</td>
								</tr>
							))}
					</Exist>
				</TBody>
			</Table>
		</Wrapper>
	);
};
